// src/components/SubscriptionGate.jsx
// Route protection based on subscription status with trial banner integration

import React from "react";
import { Navigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useSubscription } from "../context/SubscriptionContext";
import TrialBanner from "./TrialBanner";
import PaywallModal from "./PaywallModal";

/**
 * SubscriptionGate - Protects routes based on subscription status
 *
 * Uses new API fields:
 * - access_granted: Primary indicator for access
 * - trial_active: Show trial banner when true
 * - trial_ends_at: Date for trial countdown
 *
 * Behavior:
 * - Loading: Show spinner
 * - access_granted = false: Show PaywallModal
 * - trial_active = true: Show TrialBanner + content
 * - Otherwise: Show content normally
 */
export default function SubscriptionGate({ children }) {
  const { status, loading, error } = useSubscription();

  // Show loading state
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          Checking subscription...
        </Typography>
      </Box>
    );
  }

  // If no status (shouldn't happen due to fail-open in context), allow access
  if (!status) {
    return children;
  }

  // If access not granted and not in trial, show paywall
  if (!status.access_granted) {
    return (
      <>
        {children}
        <PaywallModal open={true} />
      </>
    );
  }

  // If in active trial (and no active subscription), show trial banner above content
  if (status.trial_active && status.trial_ends_at && !status.has_subscription) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <TrialBanner trialEndsAt={status.trial_ends_at} />
        <Box sx={{ flexGrow: 1 }}>{children}</Box>
      </Box>
    );
  }

  // Normal access - no banner, no modal
  return children;
}
